/**
 * AlertBoardPage (CLOUD node) — thin wrapper around the shared AlertBoardPage, which is the
 * honeycomb and the map behind one toolbar toggle. Replaces the old HexagonDashboardPage and
 * MapViewPage wrappers, which injected the same node data into two separate routes.
 *
 * Node-scoped: cameras, alerts and floor-plan layouts all come from the selected node (via
 * useApi().hexAlerts / .mapLayouts, which tunnel to it). Live video is injected as
 * HexLivePlayer, streaming over the cloud's per-node NodeConnectionManager.
 *
 * HexStreamProvider wraps the board rather than sitting inside it, so flipping the view swaps
 * the layout without dropping and re-establishing the node's WebRTC subscriptions.
 *
 * The view lives in the URL: /hexagon-alerts and /map-view both land here, so a deep link, a
 * bookmark and the sidebar's active-item highlight all keep working after the merge.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  AlertBoardPage as SharedAlertBoardPage,
  Modal,
  RecordPager,
  useApi,
  type BoardView,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '@trinetra/shared';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import HexLivePlayer, { HexStreamProvider } from '../components/hexagon/HexLivePlayer';
import { AlertDetailView } from './AlertDetailPage';

const VIEW_PATH: Record<BoardView, string> = { hex: '/hexagon-alerts', map: '/map-view' };

export interface AlertBoardPageProps {
  /**
   * Pin the board to one layout and hide the toggle. Multi-Site Alert Board uses this:
   * that page is about alerts across sites, and its toggle would navigate the operator
   * straight off it to the per-node board.
   */
  fixedView?: BoardView;
}

export default function AlertBoardPage({ fixedView }: AlertBoardPageProps) {
  const { hexAlerts, mapLayouts } = useApi();
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Clicking an alert card opens its detail HERE, in a modal, rather than routing to
  // /alerts/:id — the board is a position (which view, which hex/pin is selected, which tiles
  // are streaming) and the browser's Back button from the detail page lands on the alert
  // LIST, not back here.
  // Held as the panel's LIST plus a cursor, not one alert: an operator working a board sweeps
  // its alerts, and the record pages with the arrow keys instead of closing and re-clicking.
  const [viewing, setViewing] = useState<{ list: ViewAlertTarget[]; index: number } | null>(null);
  const current = viewing ? viewing.list[viewing.index] : null;

  // The merge does NOT merge the permissions: each layout keeps the key its route is guarded
  // with, so an operator entitled to only one of them sees that one and no toggle.
  const allowedViews: BoardView[] = fixedView
    ? [fixedView]
    : [
        ...(hasPermission('alerts.view') ? (['hex'] as BoardView[]) : []),
        ...(hasPermission('map.view') ? (['map'] as BoardView[]) : []),
      ];

  const requested: BoardView = fixedView ?? (pathname === VIEW_PATH.map ? 'map' : 'hex');
  const view = allowedViews.includes(requested) ? requested : allowedViews[0];

  // `replace` so flipping the view isn't a history entry: Back should leave the board, not
  // undo a toggle whose state the operator can already see.
  const goToView = useCallback(
    (v: BoardView) => navigate(VIEW_PATH[v], { replace: true }),
    [navigate],
  );

  // Both routes admit anyone entitled to EITHER layout (they're one page now), so a user with
  // only one of the two keys can land on the other's URL — from the sidebar, a bookmark or an
  // old link. Put the address bar back on the board they're actually being shown.
  useEffect(() => {
    if (!fixedView && view && view !== requested) goToView(view);
  }, [fixedView, view, requested, goToView]);

  return (
    <HexStreamProvider>
      <SharedAlertBoardPage
        alertsApi={hexAlerts}
        mapApi={mapLayouts}
        PlayerComponent={HexLivePlayer}
        canEdit={hasPermission('map.edit')}
        requireNode
        nodeKey={selectedNode?.nodeId}
        className="p-3 sm:p-6"
        allowedViews={allowedViews}
        // Which site's cameras these are — the board is the same for every site, so the corner
        // has to say. The local product has one site and passes nothing.
        scopeName={selectedNode?.name}
        view={view}
        // The sidebar always points at the honeycomb's URL, so an operator who works from the
        // floor plan gets put back on it instead of re-toggling every visit. Per node, since
        // only some sites have floor plans uploaded. Not on Multi-Site, which pins the view.
        // Held off until a node is selected, so the no-node state can't write (or restore
        // from) an unscoped key that then shadows every site's real preference.
        rememberView={!fixedView && !!selectedNode}
        persistKey={selectedNode?.nodeId}
        onViewChange={goToView}
        // Straight from the live preview to that camera's footage — the same jump the camera
        // grid's tiles offer. Withheld without `recordings.view`, which hides the button.
        onOpenRecordings={
          hasPermission('recordings.view')
            ? (cameraId: string) => navigate(`/recordings?camera=${cameraId}`)
            : undefined
        }
        onViewAlert={({ id, cameraId, siblings, index }: ViewAlertRequest) => {
          // No alert id means the card carries no stored row yet (the node broadcasts before
          // its storage lane writes) — there is nothing to open, so fall back to the list.
          if (!id) { navigate(`/alerts?camera_id=${cameraId}`); return; }
          // Keep the panel's list so the arrow keys can walk it. Alerts without a stored row
          // are dropped: stepping onto one would open a record that cannot be fetched.
          const list = (siblings || []).filter((s) => !!s.id);
          const at = list.findIndex((s) => s.id === id);
          setViewing(list.length ? { list, index: at >= 0 ? at : (index ?? 0) } : { list: [{ id, cameraId, name: cameraId }], index: 0 });
        }}
      />

      <Modal
        open={!!current}
        onClose={() => setViewing(null)}
        // The alert's own name, not "Alert Details": an operator working a board is deciding
        // WHICH alert this is, and the generic heading made every one of them look the same.
        title={current && (
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-brand">{current.alertName || 'Alert'}</span>
            <span className="text-[13px] font-normal text-muted-foreground">· Alert Details</span>
          </span>
        )}
        ariaLabel="Alert details"
        subtitle={current?.name}
        size="full"
        headerActions={current && viewing && (
          <>
            {/* Steps through the alerts the panel was showing when this one was opened —
                buttons and the arrow keys both. */}
            <RecordPager
              index={viewing.index}
              count={viewing.list.length}
              onIndexChange={(i) => setViewing({ list: viewing.list, index: i })}
              noun="alerts"
            />
            <button
              type="button"
              onClick={() => navigate(`/alerts/${current.id}`)}
              title="Open the full alert page"
              className="flex h-8 items-center gap-1.5 rounded-lg border border-border px-2.5 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /> Full page
            </button>
          </>
        )}
      >
        {/* Keyed on the alert id so stepping to another card refetches instead of showing
            the previous alert's data while the new one loads. */}
        {current?.id && <AlertDetailView key={current.id} alertId={current.id} showTitle={false} />}
      </Modal>
    </HexStreamProvider>
  );
}
